import { useRef, useState, useCallback } from 'react';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
import {
  Caption,
  CaptionsSettings,
  CanvasAspect,
  ClipFit,
  ClipMotion,
  ClipTransition,
  BrightnessCurve,
  ColorGrade,
  DEFAULT_FADE_SECONDS,
  captionYExpr,
} from '../types/editor';
import { atempoFilter, audioWriteName, brightnessFadeFilters, canvasSize, colorGradeFilter, evenDim, fitVideoFilter, kenBurnsFilter, xfadeName } from '../lib/editorTimeline';

function loadHtmlImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('imagem')); };
    img.src = url;
  });
}

function loadHtmlVideo(blob: Blob): Promise<{ video: HTMLVideoElement; url: string }> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    const url = URL.createObjectURL(blob);
    video.onloadeddata = () => resolve({ video, url });
    video.onerror = () => { URL.revokeObjectURL(url); reject(new Error('vídeo')); };
    video.src = url;
  });
}

function seekVideo(video: HTMLVideoElement, time: number): Promise<void> {
  return new Promise((resolve) => {
    if (Math.abs(video.currentTime - time) < 0.001) { resolve(); return; }
    const done = () => { video.removeEventListener('seeked', done); resolve(); };
    video.addEventListener('seeked', done);
    video.currentTime = time;
  });
}

function coverCanvas(img: CanvasImageSource, sw: number, sh: number, w: number, h: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  const s = Math.max(w / sw, h / sh);
  const nw = sw * s;
  const nh = sh * s;
  ctx.drawImage(img, (w - nw) / 2, (h - nh) / 2, nw, nh);
  return canvas;
}

function sampleGray(canvas: HTMLCanvasElement, dw: number, dh: number, dx = 0, dy = 0): Uint8ClampedArray {
  const out = document.createElement('canvas');
  out.width = dw;
  out.height = dh;
  const ctx = out.getContext('2d');
  if (!ctx) return new Uint8ClampedArray(dw * dh * 4);
  const sx = dw / canvas.width;
  const sy = dh / canvas.height;
  ctx.drawImage(canvas, dx * sx, dy * sy, dw, dh);
  return ctx.getImageData(0, 0, dw, dh).data;
}

function meanChannelDelta(a: Uint8ClampedArray, b: Uint8ClampedArray): number {
  let sum = 0;
  const pixels = a.length / 4;
  for (let i = 0; i < a.length; i += 4) {
    sum += Math.abs(a[i] - b[i]) + Math.abs(a[i + 1] - b[i + 1]) + Math.abs(a[i + 2] - b[i + 2]);
  }
  return sum / (pixels * 3);
}

/** Shift of the generated frame that best matches the still. (0, 0) when the gain is tiny. */
function openingShift(still: HTMLCanvasElement, frame: HTMLCanvasElement): { dx: number; dy: number } {
  const dw = 80;
  const dh = Math.max(8, Math.round(80 * frame.height / frame.width));
  const baseStill = sampleGray(still, dw, dh);
  const at = (dx: number, dy: number) => meanChannelDelta(baseStill, sampleGray(frame, dw, dh, dx, dy));
  let best = { mae: at(0, 0), dx: 0, dy: 0 };
  for (let dy = -20; dy <= 20; dy += 2) {
    for (let dx = -20; dx <= 20; dx += 2) {
      const mae = at(dx, dy);
      if (mae < best.mae) best = { mae, dx, dy };
    }
  }
  for (let dy = best.dy - 1; dy <= best.dy + 1; dy += 1) {
    for (let dx = best.dx - 1; dx <= best.dx + 1; dx += 1) {
      const mae = at(dx, dy);
      if (mae < best.mae) best = { mae, dx, dy };
    }
  }
  if (at(0, 0) - best.mae < 0.8) return { dx: 0, dy: 0 };
  return { dx: best.dx, dy: best.dy };
}

function canvasPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('png'))), 'image/png');
  });
}

/** Same shift the video filter applies: content moves, the empty edge is smeared. */
function openingShiftFilter(dx: number, dy: number, w: number, h: number): string {
  if (!dx && !dy) return '';
  const left = Math.max(0, dx);
  const right = Math.max(0, -dx);
  const top = Math.max(0, dy);
  const bottom = Math.max(0, -dy);
  const cw = w - left - right;
  const ch = h - top - bottom;
  if (cw < 2 || ch < 2) return '';
  const borders = [
    left ? `left=${left}` : '',
    right ? `right=${right}` : '',
    top ? `top=${top}` : '',
    bottom ? `bottom=${bottom}` : '',
    'mode=smear',
  ].filter(Boolean).join(':');
  return `crop=${cw}:${ch}:${right}:${bottom},pad=${w}:${h}:${left}:${top},fillborders=${borders}`;
}

const CAPTION_FONT_URLS: Record<string, string> = {
  'Inter': 'https://fonts.gstatic.com/s/inter/v13/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuFuYMZhrib2Bg-4.ttf',
  'Roboto': 'https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxKKTU1Kg.ttf',
  'Montserrat': 'https://fonts.gstatic.com/s/montserrat/v26/JTUSjIg1_i6t8kCHKm459Wlhyw.ttf',
  'Oswald': 'https://fonts.gstatic.com/s/oswald/v53/TK3_WkUHHAIjg75cFRf3bXL8LICs1_FvsUtiZTaR.ttf',
  'Playfair Display': 'https://fonts.gstatic.com/s/playfairdisplay/v30/nuFvD-vYSZviVYUb_rj3ij__anPXJzDwcbmjWBN2PKdFvXDXbtM.ttf'
};

/** Writes `font.ttf` for drawtext captions. */
async function writeCaptionFont(ffmpeg: FFmpeg, family: string) {
  try {
    const fontRes = await fetch(CAPTION_FONT_URLS[family] || CAPTION_FONT_URLS['Inter']);
    const fontBuf = await fontRes.arrayBuffer();
    await ffmpeg.writeFile('font.ttf', new Uint8Array(fontBuf));
  } catch (e) {
    console.warn('Failed to load font', e);
  }
}

async function videoFrameSize(videoBlob: Blob, fallback: { width: number; height: number }) {
  try {
    const tempVideo = document.createElement('video');
    tempVideo.src = URL.createObjectURL(videoBlob);
    await new Promise((resolve) => {
      tempVideo.onloadedmetadata = resolve;
      tempVideo.onerror = resolve;
    });
    URL.revokeObjectURL(tempVideo.src);
    if (tempVideo.videoWidth) return { width: tempVideo.videoWidth, height: tempVideo.videoHeight };
  } catch {
    /* use fallback */
  }
  return fallback;
}

/** drawtext filters for the captions; needs `font.ttf` in the FS. */
function captionFilters(captions: Caption[], settings: CaptionsSettings, videoWidth: number, videoHeight: number): string {
  return captions.map((cap) => {
    const text = cap.text.replace(/:/g, '\\:').replace(/'/g, '\u2019');
    const color = (cap.isImpactPhrase ? settings.impactTextColor : settings.textColor).replace('#', '0x');
    const bgColor = settings.bgColor.replace('#', '0x');
    const bgAlpha = Math.round(settings.bgOpacity * 255).toString(16).padStart(2, '0');
    // Font size computed in JS: commas in FFmpeg expressions break the filter chain.
    const baseSize = settings.fontSize || 45;
    const multiplier = cap.isImpactPhrase ? 1.3 : 1.0;
    const heightBasedSize = videoHeight * ((baseSize * multiplier) / 720);
    const widthBasedSize = (videoWidth * 0.9) / (Math.max(1, text.length) * 0.55);
    const finalFontSize = Math.round(Math.min(heightBasedSize, widthBasedSize));
    const yExpr = captionYExpr(cap.position || settings.position);
    return `drawtext=fontfile=font.ttf:text='${text}':fontcolor=${color}:box=1:boxborderw=12:boxcolor=${bgColor}${bgAlpha}:fontsize=${finalFontSize}:x=(w-text_w)/2:y=${yExpr}:enable='between(t,${cap.startTime},${cap.endTime})'`;
  }).join(',');
}

export type EditMixTrack = {
  blob: Blob;
  /** 0–1.5 */
  volume: number;
  /** Seconds into the video. */
  offset: number;
  loop?: boolean;
  speed?: number;
  /** Fade in/out at the edges (background music). */
  bed?: boolean;
};

export type EditMixOptions = {
  sourceVolume: number;
  captions?: Caption[];
  captionSettings?: CaptionsSettings;
  fade?: boolean;
  colorGrade?: ColorGrade;
};

export function useFFmpeg() {
  const ffmpegRef = useRef<FFmpeg | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const load = useCallback(async () => {
    if (loaded || loading) return;
    setLoading(true);
    try {
      const ffmpeg = new FFmpeg();
      ffmpeg.on('progress', ({ progress: p }) => {
        setProgress(Math.round(p * 100));
      });
      // Use CDN-hosted core to avoid bundling issues with CRA
      const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
      await ffmpeg.load({
        coreURL: `${baseURL}/ffmpeg-core.js`,
        wasmURL: `${baseURL}/ffmpeg-core.wasm`,
      });
      ffmpegRef.current = ffmpeg;
      setLoaded(true);
    } catch (err) {
      console.error('FFmpeg load error:', err);
    } finally {
      setLoading(false);
    }
  }, [loaded, loading]);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Trim video
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const trimVideo = useCallback(async (
    inputBlob: Blob, 
    startSec: number, 
    endSec: number, 
    speed: number = 1
  ): Promise<Blob | null> => {
    const ffmpeg = ffmpegRef.current;
    if (!ffmpeg) return null;

    const duration = endSec - startSec;
    await ffmpeg.writeFile('input.mp4', await fetchFile(inputBlob));

    // Build filter for speed adjustment
    const vFilter = speed !== 1 ? `-filter:v setpts=${(1 / speed).toFixed(4)}*PTS` : '';
    const aFilter = speed !== 1 ? `-filter:a atempo=${speed}` : '';

    const args = [
      '-i', 'input.mp4',
      '-ss', startSec.toFixed(3),
      '-t', duration.toFixed(3),
      ...(vFilter ? vFilter.split(' ') : []),
      ...(aFilter ? aFilter.split(' ') : []),
      '-c:v', 'libx264',
      '-preset', 'ultrafast',
      '-y', 'output.mp4'
    ];

    await ffmpeg.exec(args);
    const data = await ffmpeg.readFile('output.mp4');
    await ffmpeg.deleteFile('input.mp4');
    await ffmpeg.deleteFile('output.mp4');
    return new Blob([new Uint8Array(data as any)], { type: 'video/mp4' });
  }, []);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Probe video (or image) pixel size
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const getMediaDimensions = useCallback(async (blob: Blob): Promise<{ width: number; height: number }> => {
    if (blob.type.startsWith('image/')) {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          resolve({ width: img.naturalWidth || 1280, height: img.naturalHeight || 720 });
          URL.revokeObjectURL(img.src);
        };
        img.onerror = () => {
          resolve({ width: 1280, height: 720 });
          URL.revokeObjectURL(img.src);
        };
        img.src = URL.createObjectURL(blob);
      });
    }

    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        resolve({
          width: video.videoWidth || 1280,
          height: video.videoHeight || 720,
        });
        URL.revokeObjectURL(video.src);
      };
      video.onerror = () => {
        resolve({ width: 1280, height: 720 });
        URL.revokeObjectURL(video.src);
      };
      video.src = URL.createObjectURL(blob);
    });
  }, []);

  const imageToVideo = useCallback(async (
    imageBlob: Blob,
    durationSec: number,
    width: number,
    height: number,
    fit: ClipFit = 'cover',
    motion: ClipMotion = 'zoom-in',
  ): Promise<Blob | null> => {
    const ffmpeg = ffmpegRef.current;
    if (!ffmpeg) return null;
    const dur = Math.max(0.2, Number.isFinite(durationSec) ? durationSec : 1);
    const ext = imageBlob.type.includes('png') ? 'png' : 'jpg';
    const inName = `still.${ext}`;
    await ffmpeg.writeFile(inName, await fetchFile(imageBlob));
    const vf = kenBurnsFilter(width, height, dur, motion, fit);
    await ffmpeg.exec([
      '-loop', '1',
      '-i', inName,
      '-f', 'lavfi',
      '-i', 'anullsrc=channel_layout=stereo:sample_rate=44100',
      '-t', dur.toFixed(3),
      '-vf', vf,
      '-c:v', 'libx264', '-preset', 'ultrafast',
      '-pix_fmt', 'yuv420p',
      '-c:a', 'aac', '-ar', '44100', '-ac', '2',
      '-shortest',
      '-y', 'still.mp4',
    ]);
    const data = await ffmpeg.readFile('still.mp4');
    try { await ffmpeg.deleteFile(inName); } catch { /* ignore */ }
    try { await ffmpeg.deleteFile('still.mp4'); } catch { /* ignore */ }
    return new Blob([new Uint8Array(data as any)], { type: 'video/mp4' });
  }, []);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Concat videos onto a shared project canvas (letterbox or crop)
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const concatVideos = useCallback(async (
    blobs: Blob[],
    resolution: '720p' | '1080p' = '720p',
    options?: {
      aspect?: CanvasAspect;
      clips?: Array<{
        fit?: ClipFit;
        fadeIn?: boolean;
        fadeOut?: boolean;
        duration?: number;
        transitionOut?: ClipTransition;
        motion?: ClipMotion;
        brightnessCurve?: BrightnessCurve;
        brightnessCurveSeconds?: number;
        volume?: number;
        muted?: boolean;
      }>;
      /** Overrides DEFAULT_FADE_SECONDS for clip fades and crossfades. */
      fadeSeconds?: number;
      /** Clip fades also fade the clip's audio. */
      fadeAudio?: boolean;
      /** Throw instead of falling back to a plain concat when the crossfade graph fails. */
      strictTransitions?: boolean;
    },
  ): Promise<Blob | null> => {
    const ffmpeg = ffmpegRef.current;
    if (!ffmpeg || blobs.length === 0) return null;

    let canvasW: number;
    let canvasH: number;
    if (options?.aspect) {
      const size = canvasSize(options.aspect, resolution);
      canvasW = evenDim(size.w);
      canvasH = evenDim(size.h);
    } else {
      const dims = [];
      for (const blob of blobs) {
        dims.push(await getMediaDimensions(blob));
      }
      let maxW = Math.max(...dims.map((d) => d.width));
      let maxH = Math.max(...dims.map((d) => d.height));
      const maxLongEdge = resolution === '1080p' ? 1920 : 1280;
      const longEdge = Math.max(maxW, maxH) || 1;
      const scaleDown = longEdge > maxLongEdge ? maxLongEdge / longEdge : 1;
      canvasW = evenDim(maxW * scaleDown);
      canvasH = evenDim(maxH * scaleDown);
    }

    const fadeDur = options?.fadeSeconds && options.fadeSeconds > 0 ? options.fadeSeconds : DEFAULT_FADE_SECONDS;
    const useXfade = blobs.length > 1 && (options?.clips?.some((c) => xfadeName(c.transitionOut || 'none')) ?? false);

    for (let i = 0; i < blobs.length; i++) {
      const clip = options?.clips?.[i];
      const inName = `seg_${i}.mp4`;
      const outName = `norm_${i}.mp4`;
      await ffmpeg.writeFile(inName, await fetchFile(blobs[i]));
      const motion = clip?.motion || 'none';
      const clipLen = clip?.duration && clip.duration > 0 ? clip.duration : 1;
      const vfParts = [
        motion !== 'none'
          ? kenBurnsFilter(canvasW, canvasH, clipLen, motion, clip?.fit || 'cover')
          : fitVideoFilter(canvasW, canvasH, clip?.fit || 'cover'),
      ];
      const clipDur = clip?.duration && clip.duration > fadeDur ? clip.duration : undefined;
      const clipVol = clip?.muted ? 0 : Math.min(2, Math.max(0, Number(clip?.volume ?? 1)));
      const afParts = ['aresample=44100', 'aformat=channel_layouts=stereo', `volume=${clipVol.toFixed(2)}`];
      // xfade needs equal frame rates, and its offsets assume each input lasts exactly `duration`.
      const padTo = useXfade && clip?.duration && clip.duration > 0 ? clip.duration : undefined;
      if (useXfade) vfParts.push('fps=30');
      if (padTo) {
        vfParts.push('tpad=stop_mode=clone:stop_duration=1');
        afParts.push('apad');
      }
      if (!useXfade) {
        if (clip?.fadeIn) {
          vfParts.push(`fade=t=in:st=0:d=${fadeDur}`);
          if (options?.fadeAudio) afParts.push(`afade=t=in:st=0:d=${fadeDur}`);
        }
        if (clip?.fadeOut && clipDur) {
          const st = Math.max(0, clipDur - fadeDur).toFixed(3);
          vfParts.push(`fade=t=out:st=${st}:d=${fadeDur}`);
          if (options?.fadeAudio) afParts.push(`afade=t=out:st=${st}:d=${fadeDur}`);
        }
      }
      vfParts.push(...brightnessFadeFilters(clipLen, clip?.brightnessCurve || 'none', clip?.brightnessCurveSeconds));
      const vf = vfParts.join(',');
      const af = afParts.join(',');
      const hasAudio = await ffmpeg.exec(['-i', inName, '-map', '0:a:0', '-t', '0.1', '-f', 'null', '-']).then((c) => c === 0, () => false);
      const encode = (silent: boolean) => ffmpeg.exec([
        '-i', inName,
        ...(silent ? ['-f', 'lavfi', '-i', 'anullsrc=channel_layout=stereo:sample_rate=44100'] : []),
        '-vf', vf,
        '-af', af,
        ...(silent ? ['-map', '0:v', '-map', '1:a', '-shortest'] : []),
        ...(padTo ? ['-t', padTo.toFixed(3)] : []),
        '-c:v', 'libx264', '-preset', 'ultrafast',
        '-c:a', 'aac', '-ar', '44100', '-ac', '2',
        '-y', outName,
      ]).catch(() => 1);
      let code = hasAudio ? await encode(false) : 1;
      if (code !== 0) code = await encode(true);
      await ffmpeg.deleteFile(inName);
      if (code !== 0) throw new Error(`Falha ao preparar o vídeo ${i + 1} para juntar.`);
    }

    if (useXfade && options?.clips) {
      const inputs: string[] = [];
      for (let i = 0; i < blobs.length; i++) {
        inputs.push('-i', `norm_${i}.mp4`);
      }
      const parts: string[] = blobs.map((_, i) => `[${i}:v]settb=AVTB,fps=30[s${i}]`);
      let lastLabel = '[s0]';
      let lastAudio = '[0:a]';
      let timeline = 0;
      for (let i = 0; i < blobs.length - 1; i++) {
        const trans = xfadeName(options.clips[i]?.transitionOut || 'none') || 'fade';
        const dur = options.clips[i]?.duration || 1;
        const offset = Math.max(0, timeline + dur - fadeDur);
        const lastPair = i === blobs.length - 2;
        const vOut = lastPair ? '[vout]' : `[xf${i}]`;
        const aOut = lastPair ? '[aout]' : `[af${i}]`;
        parts.push(`${lastLabel}[s${i + 1}]xfade=transition=${trans}:duration=${fadeDur}:offset=${offset.toFixed(3)}${vOut}`);
        parts.push(`${lastAudio}[${i + 1}:a]acrossfade=d=${fadeDur}:c1=tri:c2=tri${aOut}`);
        lastLabel = vOut;
        lastAudio = aOut;
        timeline = offset;
      }
      const code = await ffmpeg.exec([
        ...inputs,
        '-filter_complex', parts.join(';'),
        '-map', '[vout]',
        '-map', '[aout]',
        '-c:v', 'libx264', '-preset', 'ultrafast',
        '-c:a', 'aac', '-ar', '44100', '-ac', '2',
        '-y', 'final.mp4',
      ]).catch(() => 1);
      if (code !== 0) {
        if (options.strictTransitions) throw new Error('Falha ao aplicar as transições entre os vídeos.');
        const normList = blobs.map((_, i) => `file 'norm_${i}.mp4'`).join('\n');
        await ffmpeg.writeFile('list.txt', new TextEncoder().encode(normList));
        await ffmpeg.exec([
          '-f', 'concat', '-safe', '0',
          '-i', 'list.txt',
          '-c', 'copy',
          '-y', 'final.mp4',
        ]);
      }
    } else {
      const normList = blobs.map((_, i) => `file 'norm_${i}.mp4'`).join('\n');
      await ffmpeg.writeFile('list.txt', new TextEncoder().encode(normList));

      await ffmpeg.exec([
        '-f', 'concat', '-safe', '0',
        '-i', 'list.txt',
        '-c', 'copy',
        '-y', 'final.mp4',
      ]);
    }

    const data = await ffmpeg.readFile('final.mp4');

    for (let i = 0; i < blobs.length; i++) {
      try { await ffmpeg.deleteFile(`norm_${i}.mp4`); } catch { /* ignore */ }
    }
    try { await ffmpeg.deleteFile('list.txt'); } catch { /* ignore */ }
    try { await ffmpeg.deleteFile('final.mp4'); } catch { /* ignore */ }

    return new Blob([new Uint8Array(data as any)], { type: 'video/mp4' });
  }, [getMediaDimensions]);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Change speed (video + audio)
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const changeSpeed = useCallback(async (blob: Blob, speed: number): Promise<Blob | null> => {
    const ffmpeg = ffmpegRef.current;
    if (!ffmpeg || speed === 1) return blob;

    await ffmpeg.writeFile('input.mp4', await fetchFile(blob));

    // atempo only supports 0.5 to 2.0 range, chain for larger values
    let atempoFilters: string[] = [];
    let s = speed;
    while (s > 2.0) { atempoFilters.push('atempo=2.0'); s /= 2.0; }
    while (s < 0.5) { atempoFilters.push('atempo=0.5'); s *= 2.0; }
    atempoFilters.push(`atempo=${s.toFixed(4)}`);

    await ffmpeg.exec([
      '-i', 'input.mp4',
      '-filter:v', `setpts=${(1 / speed).toFixed(4)}*PTS`,
      '-filter:a', atempoFilters.join(','),
      '-c:v', 'libx264', '-preset', 'ultrafast',
      '-c:a', 'aac',
      '-y', 'output.mp4'
    ]);

    const data = await ffmpeg.readFile('output.mp4');
    await ffmpeg.deleteFile('input.mp4');
    await ffmpeg.deleteFile('output.mp4');
    return new Blob([new Uint8Array(data as any)], { type: 'video/mp4' });
  }, []);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Add/mix audio track to video
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const mixAudio = useCallback(async (
    videoBlob: Blob, 
    audioBlob: Blob, 
    audioVolume: number = 0.5,
    videoVolume: number = 1.0,
    audioOffset: number = 0
  ): Promise<Blob | null> => {
    const ffmpeg = ffmpegRef.current;
    if (!ffmpeg) return null;

    await ffmpeg.writeFile('video.mp4', await fetchFile(videoBlob));
    const audioName = audioWriteName(audioBlob, 'audio');
    await ffmpeg.writeFile(audioName, await fetchFile(audioBlob));

    const filterComplex = [
      `[0:a]volume=${videoVolume.toFixed(2)}[va]`,
      `[1:a]adelay=${Math.round(audioOffset * 1000)}|${Math.round(audioOffset * 1000)},volume=${audioVolume.toFixed(2)}[aa]`,
      `[va][aa]amix=inputs=2:duration=first:dropout_transition=2[aout]`
    ].join(';');

    await ffmpeg.exec([
      '-i', 'video.mp4',
      '-i', audioName,
      '-filter_complex', filterComplex,
      '-map', '0:v',
      '-map', '[aout]',
      '-c:v', 'copy',
      '-c:a', 'aac',
      '-shortest',
      '-y', 'output.mp4'
    ]);

    const data = await ffmpeg.readFile('output.mp4');
    await ffmpeg.deleteFile('video.mp4');
    await ffmpeg.deleteFile(audioName);
    await ffmpeg.deleteFile('output.mp4');
    return new Blob([new Uint8Array(data as any)], { type: 'video/mp4' });
  }, []);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Split video at time
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const splitVideo = useCallback(async (blob: Blob, atTimeSec: number): Promise<[Blob, Blob] | null> => {
    const ffmpeg = ffmpegRef.current;
    if (!ffmpeg) return null;

    await ffmpeg.writeFile('input.mp4', await fetchFile(blob));

    // Part 1: 0 to atTimeSec
    await ffmpeg.exec([
      '-i', 'input.mp4',
      '-t', atTimeSec.toFixed(3),
      '-c:v', 'libx264', '-preset', 'ultrafast',
      '-c:a', 'aac',
      '-y', 'part1.mp4'
    ]);

    // Part 2: atTimeSec to end
    await ffmpeg.exec([
      '-i', 'input.mp4',
      '-ss', atTimeSec.toFixed(3),
      '-c:v', 'libx264', '-preset', 'ultrafast',
      '-c:a', 'aac',
      '-y', 'part2.mp4'
    ]);

    const data1 = await ffmpeg.readFile('part1.mp4');
    const data2 = await ffmpeg.readFile('part2.mp4');

    await ffmpeg.deleteFile('input.mp4');
    await ffmpeg.deleteFile('part1.mp4');
    await ffmpeg.deleteFile('part2.mp4');

    return [
      new Blob([new Uint8Array(data1 as any)], { type: 'video/mp4' }),
      new Blob([new Uint8Array(data2 as any)], { type: 'video/mp4' })
    ];
  }, []);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Generate thumbnail from video
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const generateThumbnail = useCallback(async (blob: Blob, atTimeSec: number = 0): Promise<string> => {
    const ffmpeg = ffmpegRef.current;
    if (!ffmpeg) return '';

    await ffmpeg.writeFile('input.mp4', await fetchFile(blob));

    await ffmpeg.exec([
      '-i', 'input.mp4',
      '-ss', atTimeSec.toFixed(3),
      '-frames:v', '1',
      '-vf', 'scale=160:-1',
      '-y', 'thumb.jpg'
    ]);

    const data = await ffmpeg.readFile('thumb.jpg');
    await ffmpeg.deleteFile('input.mp4');
    await ffmpeg.deleteFile('thumb.jpg');

    const thumbBlob = new Blob([new Uint8Array(data as any)], { type: 'image/jpeg' });
    return URL.createObjectURL(thumbBlob);
  }, []);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Full export with audio mixing
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const exportFinal = useCallback(async (
    videoBlob: Blob,
    audioTracks: Array<{ blob: Blob; volume: number; offset: number; loop: boolean; speed?: number }>,
    resolution: '720p' | '1080p' = '720p',
    options?: { captions: Caption[]; settings: CaptionsSettings; colorGrade?: ColorGrade; durationSec?: number }
  ): Promise<Blob | null> => {
    const ffmpeg = ffmpegRef.current;
    if (!ffmpeg) return null;

    await ffmpeg.writeFile('video.mp4', await fetchFile(videoBlob));

    const hasCaptions = options?.captions && options.captions.length > 0;
    let videoFilter = '';
    
    if (hasCaptions && options) {
      await writeCaptionFont(ffmpeg, options.settings.fontFamily);
      const { width, height } = await videoFrameSize(videoBlob, { width: 720, height: 1280 });
      videoFilter = captionFilters(options.captions, options.settings, width, height);
    }

    const grade = colorGradeFilter(options?.colorGrade || 'none');
    const vfBits = [grade, videoFilter].filter(Boolean);
    const pieceDur = options?.durationSec;
    if (pieceDur && pieceDur > 1.6) {
      vfBits.push('fade=t=in:st=0:d=0.6');
      vfBits.push(`fade=t=out:st=${(pieceDur - 0.9).toFixed(3)}:d=0.9`);
    }
    videoFilter = vfBits.join(',');

    if (audioTracks.length === 0) {
      // No extra audio tracks
      const args = [
        '-i', 'video.mp4',
        '-c:v', 'libx264', '-preset', 'ultrafast'
      ];
      if (videoFilter) {
        args.push('-vf', videoFilter);
      }
      args.push('-c:a', 'aac', '-y', 'final.mp4');
      await ffmpeg.exec(args);
    } else {
      // With audio tracks
      const inputs = ['-i', 'video.mp4'];
      const filterParts: string[] = [];
      const audioInputs: string[] = [];

      for (let i = 0; i < audioTracks.length; i++) {
        const track = audioTracks[i];
        const audioName = audioWriteName(track.blob, `audio_${i}`);
        await ffmpeg.writeFile(audioName, await fetchFile(track.blob));
        inputs.push('-i', audioName);

        const delay = Math.round(track.offset * 1000);
        const tempo = atempoFilter(track.speed || 1);
        const tempoBit = tempo ? `${tempo},` : '';
        const loopFilter = track.loop ? ',aloop=loop=-1:size=2e+09' : '';
        filterParts.push(
          `[${i + 1}:a]aresample=44100,aformat=channel_layouts=stereo,adelay=${delay}|${delay},${tempoBit}volume=${track.volume.toFixed(2)}${loopFilter}[a${i}]`
        );
        audioInputs.push(`[a${i}]`);
      }

      filterParts.push(`[0:a]aresample=44100,aformat=channel_layouts=stereo,volume=1.0[va]`);
      const allInputs = ['[va]', ...audioInputs].join('');
      filterParts.push(`${allInputs}amix=inputs=${audioTracks.length + 1}:duration=first:dropout_transition=2:normalize=0[aout]`);

      const args = [
        ...inputs,
        '-filter_complex', filterParts.join(';'),
        '-map', '0:v',
        '-map', '[aout]',
        '-c:v', 'libx264', '-preset', 'ultrafast'
      ];
      if (videoFilter) {
        args.push('-vf', videoFilter);
      }
      args.push('-c:a', 'aac', '-shortest', '-y', 'final.mp4');
      
      await ffmpeg.exec(args);

      // Cleanup extra audio files
      for (let i = 0; i < audioTracks.length; i++) {
        try { await ffmpeg.deleteFile(audioWriteName(audioTracks[i].blob, `audio_${i}`)); } catch {}
      }
    }

    const data = await ffmpeg.readFile('final.mp4');
    
    // Cleanup
    await ffmpeg.deleteFile('video.mp4');
    await ffmpeg.deleteFile('final.mp4');
    if (hasCaptions) {
      try { await ffmpeg.deleteFile('font.ttf'); } catch {}
    }

    return new Blob([new Uint8Array(data as any)], { type: 'video/mp4' });
  }, []);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Get video duration from blob
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const getVideoDuration = useCallback(async (blob: Blob): Promise<number> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      let settled = false;
      const finish = (raw: number) => {
        if (settled) return;
        settled = true;
        URL.revokeObjectURL(video.src);
        resolve(Number.isFinite(raw) && raw > 0 ? raw : 0);
      };
      video.onloadedmetadata = () => {
        if (Number.isFinite(video.duration) && video.duration > 0) {
          finish(video.duration);
          return;
        }
        video.ontimeupdate = () => {
          video.ontimeupdate = null;
          const d = video.duration;
          video.currentTime = 0;
          finish(d);
        };
        try {
          video.currentTime = 1e101;
        } catch {
          finish(0);
        }
      };
      video.onerror = () => finish(0);
      window.setTimeout(() => finish(0), 8000);
      video.src = URL.createObjectURL(blob);
    });
  }, []);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Get audio duration from blob
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const getAudioDuration = useCallback(async (blob: Blob): Promise<number> => {
    return new Promise((resolve) => {
      const audio = document.createElement('audio');
      audio.preload = 'metadata';
      audio.onloadedmetadata = () => {
        resolve(audio.duration);
        URL.revokeObjectURL(audio.src);
      };
      audio.onerror = () => {
        resolve(0);
        URL.revokeObjectURL(audio.src);
      };
      audio.src = URL.createObjectURL(blob);
    });
  }, []);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Mix sound tracks over a finished video, keeping its own audio
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const mixEditedVideo = useCallback(async (
    videoBlob: Blob,
    tracks: Array<EditMixTrack & { seconds?: number }>,
    options: EditMixOptions,
  ): Promise<Blob | null> => {
    const ffmpeg = ffmpegRef.current;
    if (!ffmpeg) return null;
    const duration = await getVideoDuration(videoBlob);
    if (!duration) throw new Error('Não consegui ler a duração do vídeo.');

    await ffmpeg.writeFile('video.mp4', await fetchFile(videoBlob));
    const hasAudio = (await ffmpeg.exec(['-i', 'video.mp4', '-map', '0:a:0', '-t', '0.1', '-f', 'null', '-'])) === 0;

    const inputs = ['-i', 'video.mp4'];
    const names: string[] = [];
    const parts: string[] = [
      hasAudio
        ? `[0:a]aresample=44100,aformat=channel_layouts=stereo,volume=${Math.max(0, options.sourceVolume).toFixed(2)}[va]`
        : `anullsrc=r=44100:cl=stereo,atrim=0:${duration.toFixed(3)}[va]`,
    ];
    for (let i = 0; i < tracks.length; i++) {
      const t = tracks[i];
      const name = audioWriteName(t.blob, `track_${i}`);
      names.push(name);
      await ffmpeg.writeFile(name, await fetchFile(t.blob));
      inputs.push('-i', name);
      const offset = Math.max(0, Math.min(duration - 0.1, t.offset || 0));
      const span = duration - offset;
      const speed = t.speed && t.speed > 0 ? t.speed : 1;
      const played = t.loop || !t.seconds ? span : Math.min(span, t.seconds / speed);
      const chain = ['aresample=44100', 'aformat=channel_layouts=stereo'];
      const tempo = atempoFilter(speed);
      if (tempo) chain.push(tempo);
      if (t.loop) chain.push('aloop=loop=-1:size=2e+09');
      chain.push(`atrim=0:${span.toFixed(3)}`, 'asetpts=PTS-STARTPTS');
      if (t.bed && played > 2) {
        chain.push('afade=t=in:st=0:d=0.4', `afade=t=out:st=${(played - 1.5).toFixed(3)}:d=1.5`);
      }
      const delay = Math.round(offset * 1000);
      if (delay > 0) chain.push(`adelay=${delay}|${delay}`);
      chain.push(`volume=${Math.max(0, t.volume).toFixed(2)}`);
      parts.push(`[${i + 1}:a]${chain.join(',')}[a${i}]`);
    }
    // Container duration can run ~0.2s past the last video frame; finish the fade before it.
    const fadeOutStart = Math.max(0.6, duration - 1.15).toFixed(3);
    const outFade = options.fade && duration > 1.6 ? `,afade=t=out:st=${fadeOutStart}:d=0.9` : '';
    parts.push(
      `[va]${tracks.map((_, i) => `[a${i}]`).join('')}amix=inputs=${tracks.length + 1}:duration=first:dropout_transition=0:normalize=0,alimiter=limit=0.95${outFade}[aout]`,
    );

    const vf: string[] = [];
    const grade = colorGradeFilter(options.colorGrade || 'none');
    if (grade) vf.push(grade);
    if (options.captions?.length && options.captionSettings) {
      await writeCaptionFont(ffmpeg, options.captionSettings.fontFamily);
      const { width, height } = await videoFrameSize(videoBlob, { width: 1280, height: 720 });
      vf.push(captionFilters(options.captions, options.captionSettings, width, height));
    }
    if (options.fade && duration > 1.6) {
      vf.push('fade=t=in:st=0:d=0.6', `fade=t=out:st=${fadeOutStart}:d=0.9`);
    }

    const args = [
      ...inputs,
      '-filter_complex', parts.join(';'),
      '-map', '0:v',
      '-map', '[aout]',
    ];
    if (vf.length) args.push('-vf', vf.join(','), '-c:v', 'libx264', '-preset', 'ultrafast', '-pix_fmt', 'yuv420p');
    else args.push('-c:v', 'copy');
    args.push('-c:a', 'aac', '-b:a', '192k', '-movflags', '+faststart', '-t', duration.toFixed(3), '-y', 'edited.mp4');
    const code = await ffmpeg.exec(args);

    const cleanup = async () => {
      for (const f of ['video.mp4', 'edited.mp4', 'font.ttf', ...names]) {
        try { await ffmpeg.deleteFile(f); } catch { /* not written */ }
      }
    };
    if (code !== 0) {
      await cleanup();
      throw new Error('Falha ao mixar o áudio no vídeo.');
    }
    const data = await ffmpeg.readFile('edited.mp4');
    await cleanup();
    return new Blob([new Uint8Array(data as any)], { type: 'video/mp4' });
  }, [getVideoDuration]);

  /**
   * Contínuo: a foto original fica 6 quadros, o minterpolate liga em mais 6,
   * e o vídeo segue a partir do segundo quadro. Um deslocamento pequeno é corrigido.
   */
  const bridgeOpening = useCallback(async (stillBlob: Blob, videoBlob: Blob): Promise<Blob | null> => {
    const ffmpeg = ffmpegRef.current;
    if (!ffmpeg) return null;
    const [img, loaded] = await Promise.all([loadHtmlImage(stillBlob), loadHtmlVideo(videoBlob)]);
    const video = loaded.video;
    try {
      await seekVideo(video, 0);
      const w = evenDim(video.videoWidth || 720);
      const h = evenDim(video.videoHeight || 1280);
      const stillCanvas = coverCanvas(img, img.naturalWidth || w, img.naturalHeight || h, w, h);
      const frameCanvas = coverCanvas(video, video.videoWidth || w, video.videoHeight || h, w, h);
      const shift = openingShift(stillCanvas, frameCanvas);
      const [stillPng, framePng] = await Promise.all([canvasPng(stillCanvas), canvasPng(frameCanvas)]);

      await ffmpeg.writeFile('bridge-in.mp4', await fetchFile(videoBlob));
      await ffmpeg.writeFile('bridge-still.png', await fetchFile(stillPng));
      await ffmpeg.writeFile('bridge-frame.png', await fetchFile(framePng));
      let logged = '';
      const onLog = ({ message }: { message: string }) => { logged += `${message}\n`; };
      ffmpeg.on('log', onLog);
      try {
        await ffmpeg.exec(['-i', 'bridge-in.mp4']);
      } finally {
        ffmpeg.off('log', onLog);
      }
      const fpsMatch = logged.match(/(\d+(?:\.\d+)?)\s*fps/);
      const fps = Math.min(60, Math.max(8, Math.round(Number(fpsMatch?.[1]) || 24)));
      const hasAudio = /Audio:/.test(logged);
      const pair = fps / 6;
      const span = (2 / pair).toFixed(3);
      const inputSpan = (3 / pair).toFixed(3);
      const gap = (1 / pair).toFixed(3);
      const bridgeDur = (6 / fps).toFixed(3);
      const moved = openingShiftFilter(shift.dx, shift.dy, w, h);

      const run = (useShift: boolean, withAudio: boolean) => {
        const shiftChain = useShift && moved ? `,${moved}` : '';
        const graph = [
          `[0:v]setsar=1,fps=${fps}${shiftChain}[vid]`,
          `[1:v]scale=${w}:${h}:flags=bicubic,setsar=1,fps=${fps},trim=end_frame=6,setpts=PTS-STARTPTS[hold]`,
          `[1:v]scale=${w}:${h}:flags=bicubic,setsar=1,fps=${pair},trim=duration=${span},setpts=PTS-STARTPTS[ss]`,
          `[2:v]scale=${w}:${h}:flags=bicubic,setsar=1${shiftChain},fps=${pair},trim=duration=${span},setpts=PTS-STARTPTS[ee]`,
          `[ss][ee]concat=n=2:v=1:a=0,minterpolate=fps=${fps}:mi_mode=mci:mc_mode=obmc:me_mode=bidir:vsbmc=0,trim=start=${gap}:duration=${bridgeDur},setpts=PTS-STARTPTS[bridge]`,
          `[vid]trim=start_frame=1,setpts=PTS-STARTPTS[tail]`,
          `[hold][bridge][tail]concat=n=3:v=1:a=0[v]`,
        ];
        if (withAudio) {
          const delayMs = Math.round((11 / fps) * 1000);
          graph.push(`[0:a]atrim=start=${(1 / fps).toFixed(3)},asetpts=PTS-STARTPTS,adelay=${delayMs}|${delayMs}[a]`);
        }
        return ffmpeg.exec([
          '-i', 'bridge-in.mp4',
          '-loop', '1', '-framerate', String(pair), '-t', inputSpan, '-i', 'bridge-still.png',
          '-loop', '1', '-framerate', String(pair), '-t', inputSpan, '-i', 'bridge-frame.png',
          '-filter_complex', graph.join(';'),
          '-map', '[v]',
          ...(withAudio ? ['-map', '[a]', '-c:a', 'aac', '-b:a', '128k'] : ['-an']),
          '-c:v', 'libx264', '-preset', 'veryfast', '-crf', '18', '-pix_fmt', 'yuv420p',
          '-movflags', '+faststart', '-y', 'bridge-out.mp4',
        ]);
      };

      const shifted = Boolean(moved);
      let code = await run(shifted, hasAudio);
      if (code !== 0 && hasAudio) code = await run(shifted, false);
      if (code !== 0 && shifted) code = await run(false, false);
      if (code !== 0) throw new Error('Falha ao ligar a imagem inicial.');
      const data = await ffmpeg.readFile('bridge-out.mp4');
      return new Blob([new Uint8Array(data as Uint8Array)], { type: 'video/mp4' });
    } finally {
      URL.revokeObjectURL(loaded.url);
      for (const name of ['bridge-in.mp4', 'bridge-still.png', 'bridge-frame.png', 'bridge-out.mp4']) {
        try { await ffmpeg.deleteFile(name); } catch { /* not written */ }
      }
    }
  }, []);

  /**
   * Key out a solid-color background on a finished clip and place it over a still plate.
   * `keyHex` is `0xRRGGBB`; similarity/blend scale with how flat the key is.
   */
  const compositeVideoOnBackground = useCallback(async (
    videoBlob: Blob,
    backgroundBlob: Blob,
    opts: { keyHex: string; variance?: number },
  ): Promise<Blob | null> => {
    const ffmpeg = ffmpegRef.current;
    if (!ffmpeg) return null;
    const duration = await getVideoDuration(videoBlob);
    if (!duration) throw new Error('Não consegui ler a duração do vídeo.');
    const { width, height } = await videoFrameSize(videoBlob, { width: 1280, height: 720 });
    const w = evenDim(width);
    const h = evenDim(height);
    const variance = opts.variance ?? 10;
    // Flat studio keys (white/black) need a tighter similarity; noisier plates a bit looser.
    const similarity = variance < 8 ? 0.12 : variance < 16 ? 0.22 : 0.32;
    const blend = variance < 8 ? 0.08 : 0.14;
    const hex = opts.keyHex.startsWith('0x') ? opts.keyHex : `0x${opts.keyHex.replace(/^#/, '')}`;

    await ffmpeg.writeFile('fg.mp4', await fetchFile(videoBlob));
    const bgName = backgroundBlob.type.includes('png') ? 'bg.png' : 'bg.jpg';
    await ffmpeg.writeFile(bgName, await fetchFile(backgroundBlob));
    const hasAudio = (await ffmpeg.exec(['-i', 'fg.mp4', '-map', '0:a:0', '-t', '0.1', '-f', 'null', '-'])) === 0;

    const graph = [
      `[1:v]scale=${w}:${h}:force_original_aspect_ratio=increase,crop=${w}:${h},setsar=1[bg]`,
      `[0:v]scale=${w}:${h}:force_original_aspect_ratio=decrease,pad=${w}:${h}:(ow-iw)/2:(oh-ih)/2,setsar=1,colorkey=${hex}:${similarity.toFixed(3)}:${blend.toFixed(3)},format=yuva420p[fg]`,
      `[bg][fg]overlay=0:0:format=auto,format=yuv420p[vout]`,
    ].join(';');

    const args = [
      '-i', 'fg.mp4',
      '-loop', '1', '-i', bgName,
      '-filter_complex', graph,
      '-map', '[vout]',
      ...(hasAudio ? ['-map', '0:a:0', '-c:a', 'aac', '-b:a', '192k'] : ['-an']),
      '-c:v', 'libx264', '-preset', 'veryfast', '-crf', '18', '-pix_fmt', 'yuv420p',
      '-movflags', '+faststart', '-shortest', '-t', duration.toFixed(3), '-y', 'keyed.mp4',
    ];
    const code = await ffmpeg.exec(args);
    const cleanup = async () => {
      for (const f of ['fg.mp4', bgName, 'keyed.mp4']) {
        try { await ffmpeg.deleteFile(f); } catch { /* */ }
      }
    };
    if (code !== 0) {
      await cleanup();
      throw new Error('Falha ao aplicar o fundo no vídeo.');
    }
    const data = await ffmpeg.readFile('keyed.mp4');
    await cleanup();
    return new Blob([new Uint8Array(data as Uint8Array)], { type: 'video/mp4' });
  }, [getVideoDuration]);

  return { 
    ffmpeg: ffmpegRef.current, 
    loaded, loading, progress, 
    load, 
    trimVideo, concatVideos, imageToVideo, changeSpeed, 
    mixAudio, splitVideo, generateThumbnail, 
    exportFinal, getVideoDuration, getAudioDuration,
    mixEditedVideo,
    bridgeOpening,
    compositeVideoOnBackground,
  };
}
